import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import { getBusinessMetrics, getUserMetrics, getValetMetrics, getValets } from './services/adminService';
import BusinessTab from './components/admin/BusinessTab';
import UsersTab from './components/admin/UsersTab';
import ValetsTab from './components/admin/ValetsTab';

const PERIODS = ['Today', '7 days', '30 days', '90 days'];
const TAB_NAMES = ['Business', 'Users', 'Valets'];

const DashboardRoot = styled(Box)({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#1a1a1a',
  minHeight: '100vh',
  backgroundColor: '#f8f8f6',
});

const Nav = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 18px',
  borderBottom: '0.5px solid #e0ddd6',
  backgroundColor: '#ffffff',
});

const LogoBox = styled(Box)({
  width: 26,
  height: 26,
  borderRadius: 6,
  backgroundColor: '#1a1a1a',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 500,
});

const TabBar = styled(Box)({
  borderBottom: '0.5px solid #e0ddd6',
  backgroundColor: '#ffffff',
});

const StyledTab = styled(Tab)({
  padding: '9px 16px',
  fontSize: '13px',
  textTransform: 'none',
  color: '#6b6b68',
  minHeight: 'auto',
  '&.Mui-selected': {
    color: '#1a1a1a',
    fontWeight: 500,
  },
});

const Body = styled(Box)({
  padding: '18px',
  backgroundColor: '#f4f3ef',
  minHeight: 'calc(100vh - 100px)',
});

const PeriodToggle = styled(ToggleButton)({
  padding: '3px 10px',
  borderRadius: '8px !important',
  fontSize: '11px',
  textTransform: 'none',
  border: '0.5px solid #e0ddd6 !important',
  color: '#6b6b68',
  '&.Mui-selected': {
    backgroundColor: '#eceae4',
    color: '#1a1a1a',
    border: '0.5px solid #888 !important',
  },
});

const DateLabel = styled(Typography)({
  fontSize: '11px',
  color: '#9b9b97',
  fontFamily: '"SF Mono", "Fira Code", monospace',
});

const ErrorMessage = styled(Typography)({
  color: '#993C1D',
  fontSize: '13px',
  padding: '20px',
  textAlign: 'center',
});

export default function AdminDashboard() {
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState('7 days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bizData, setBizData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [valetData, setValetData] = useState(null);
  const [rosterData, setRosterData] = useState(null);
  const [rosterError, setRosterError] = useState(null);

  const fetchData = useCallback(async (selectedPeriod) => {
    setLoading(true);
    setError(null);
    try {
      const [bizRes, userRes, valetRes] = await Promise.all([
        getBusinessMetrics(selectedPeriod),
        getUserMetrics(selectedPeriod),
        getValetMetrics(selectedPeriod),
      ]);
      setBizData(bizRes.success ? bizRes.data : bizRes);
      setUserData(userRes.success ? userRes.data : userRes);
      setValetData(valetRes.success ? valetRes.data : valetRes);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
  }, [period, fetchData]);

  // Lazy-load roster when Valets tab opens.
  useEffect(() => {
    if (tab === 2 && rosterData === null && rosterError === null) {
      getValets()
        .then((res) => setRosterData(res && res.success ? res.data : res))
        .catch((err) => setRosterError(err.message || 'Failed to load valet roster'));
    }
  }, [tab, rosterData, rosterError]);

  const handlePeriodChange = (_, newPeriod) => {
    if (newPeriod) setPeriod(newPeriod);
  };

  const dateStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <DashboardRoot>
      {/* Nav bar */}
      <Nav>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <LogoBox>V</LogoBox>
          <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>Valet Network Inc.</Typography>
          <Typography sx={{ color: '#9b9b97', fontSize: '12px' }}>/ Analytics</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ gap: '4px' }}
          >
            {PERIODS.map((p) => (
              <PeriodToggle key={p} value={p}>{p}</PeriodToggle>
            ))}
          </ToggleButtonGroup>
          <DateLabel>{dateStr}</DateLabel>
        </Box>
      </Nav>

      {/* Tab bar */}
      <TabBar>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 'auto',
            px: '18px',
            '& .MuiTabs-indicator': { backgroundColor: '#1a1a1a' },
          }}
        >
          {TAB_NAMES.map((name) => (
            <StyledTab key={name} label={name} disableRipple />
          ))}
        </Tabs>
      </TabBar>

      {/* Body */}
      <Body>
        {tab <= 2 && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress sx={{ color: '#1a1a1a' }} />
          </Box>
        ) : tab <= 2 && error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <>
            {tab === 0 && <BusinessTab data={bizData} />}
            {tab === 1 && <UsersTab data={userData} />}
            {tab === 2 && <ValetsTab data={valetData} rosterData={rosterData} rosterError={rosterError} />}
          </>
        )}
      </Body>
    </DashboardRoot>
  );
}
